import { Box, IconButton, Typography } from "@mui/material";
import React, { FC, useState } from "react";
import Icons from "../../../assets/Icons";
import { useTranslation } from "react-i18next";
import Divider from "../../../components/Divider";
import { useLocation, useNavigate } from "react-router";
import DropDownMenu from "../../../components/DropDownMenu";
import { USER_ROLES } from "../../userModule/types/CommonTypes";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import Button from "../../../components/Button";
import { ChevronLeft } from "@mui/icons-material";
import CommonModal from "../../../components/CommonModal";
import { headerActionButtonSx } from "../../../components/ScreenTopBar";
import formatDateTime from "../../../helpers/formatDateTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

type ValidationIssue = {
  type: "missing" | "sequence" | "amPm" | "minGap";
  milestoneName: string;
  milestoneTime?: string;
  prevMilestoneName?: string;
  prevMilestoneTime?: string;
  isHardBlock?: boolean;
  minimumGapMinutes?: number;
};

interface CaseDetailHeaderProps {
  mrn?: string;
  surgeon?: string;
  procedureName?: string;
  caseId?: string;
  back?: boolean;
  backToTab?: number | string;
  enableDropDownExport?: boolean;
  onExport?: () => void;
  onExportCpt?: (isCSVExport?: boolean) => void;
  isEditMode?: boolean;
  onEditModeChange?: (isEditMode: boolean) => void;
  showEdit?: boolean;
  showExport?: boolean;
  caseSummaryRef?: React.MutableRefObject<any>;
}

const headerMetaTextSx = {
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "normal",
  color: "#101828",
};

const headerTitleTextSx = {
  fontFamily: "Inter",
  fontSize: "18px",
  fontWeight: 700,
  lineHeight: "24px",
  color: "#101828",
};

const headerDividerSx = {
  width: "1px",
  height: 40,
  backgroundColor: "#E5E7EB",
};

const CaseDetailHeader: FC<CaseDetailHeaderProps> = ({
  mrn,
  surgeon,
  procedureName,
  caseId,
  back = true,
  backToTab,
  enableDropDownExport = false,
  onExport,
  onExportCpt,
  isEditMode = false,
  onEditModeChange,
  showEdit = true,
  showExport = true,
  caseSummaryRef,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthValue();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>(
    []
  );
  const [isSubmitConfirmModalOpen, setIsSubmitConfirmModalOpen] =
    useState(false);
  const [hasHardBlockIssue, setHasHardBlockIssue] = useState(false);
  const [showAutoCloseHeadingFromRef, setShowAutoCloseHeadingFromRef] =
    useState(false);

  const isCodingAdmin = user?.role === USER_ROLES.CODING_ADMIN;

  const displayMRN =
    isCodingAdmin && mrn
      ? mrn.slice(0, -4).replace(/[a-zA-Z0-9]/g, "*") + mrn.slice(-4)
      : mrn;

  const handleExport = (
    event: React.MouseEvent<HTMLButtonElement>,
    isDropDownExport?: boolean
  ) => {
    if (isDropDownExport) {
      setAnchorEl(event.currentTarget);
    } else {
      onExport?.();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCptExportPDF = () => {
    onExportCpt?.();
    handleClose();
  };

  const handleCptExportCSV = () => {
    onExportCpt?.(true);
    handleClose();
  };

  const exportMenuItems = [
    {
      label: "Export as PDF",
      onClick: handleCptExportPDF,
      icon: <Icons.Pdf />,
    },
    {
      label: "Export as CSV",
      onClick: handleCptExportCSV,
      icon: <Icons.Csv />,
    },
  ];
  const location = useLocation();

  const handleEditClick = () => {
    if (!isEditMode) {
      onEditModeChange?.(true);
      return;
    }

    const summaryInstance = caseSummaryRef?.current;
    const summaryValidation =
      summaryInstance &&
      typeof summaryInstance.getMilestoneValidationSummary === "function"
        ? summaryInstance.getMilestoneValidationSummary()
        : null;

    const autoCloseToggle =
      summaryInstance &&
      typeof summaryInstance.getShouldShowAutoCloseToggle === "function"
        ? summaryInstance.getShouldShowAutoCloseToggle()
        : false;
    setShowAutoCloseHeadingFromRef(autoCloseToggle);

    if (!summaryValidation) {
      onEditModeChange?.(false);
      return;
    }

    const {
      missingRequired = [],
      sequenceIssues = [],
      amPmIssues = [],
      minimumGapIssues = [],
    } = summaryValidation;

    const issues: ValidationIssue[] = [];

    missingRequired.forEach((m: any) => {
      const name = m.title || m.milestoneKey || "";
      if (name) {
        issues.push({
          type: "missing",
          milestoneName: name,
          isHardBlock: true,
        });
      }
    });

    minimumGapIssues.forEach((m: any) => {
      const name = m.title || m.milestoneKey || "";
      const prevName = m.priorMilestoneName || "";
      const time = formatDateTime(m.timestamp, true, "hh:mm:ss aaa");
      const prevTime = formatDateTime(m.priorMilestoneTime, true, "hh:mm:ss aaa");
      if (name && prevName && time && prevTime) {
        issues.push({
          type: "minGap",
          milestoneName: name,
          milestoneTime: time,
          prevMilestoneName: prevName,
          prevMilestoneTime: prevTime,
          isHardBlock: false,
          minimumGapMinutes: m.minimumGapMinutes,
        });
      }
    });

    sequenceIssues.forEach((m: any) => {
      const name = m.title || m.milestoneKey || "";
      const prevName = m.priorMilestoneName || "";
      const time = formatDateTime(m.timestamp, true, "hh:mm:ss aaa");
      const prevTime = formatDateTime(m.priorMilestoneTime, true, "hh:mm:ss aaa");
      if (name && prevName && time && prevTime) {
        issues.push({
          type: "sequence",
          milestoneName: name,
          milestoneTime: time,
          prevMilestoneName: prevName,
          prevMilestoneTime: prevTime,
          isHardBlock: Boolean(m.isHardBlock),
        });
      }
    });

    amPmIssues.forEach((m: any) => {
      const name = m.title || m.milestoneKey || "";
      const prevName = m.priorMilestoneName || "";
      const time = formatDateTime(m.timestamp, true, "hh:mm:ss aaa");
      const prevTime = formatDateTime(m.priorMilestoneTime, true, "hh:mm:ss aaa");
      if (name && prevName && time && prevTime) {
        issues.push({
          type: "amPm",
          milestoneName: name,
          milestoneTime: time,
          prevMilestoneName: prevName,
          prevMilestoneTime: prevTime,
          isHardBlock: false,
        });
      }
    });

    if (issues.length > 0 || autoCloseToggle) {
      setValidationIssues(issues);
      setHasHardBlockIssue(issues.some((issue) => issue.isHardBlock));
      setIsValidationModalOpen(true);
      setIsSubmitConfirmModalOpen(false);
      return;
    }

    setHasHardBlockIssue(false);
    setIsValidationModalOpen(false);
    setIsSubmitConfirmModalOpen(true);
  };

  const handleBackNavigation = () => {
    const from = location.state?.from;
    if (from?.path) {
      navigate(from.path, {
        replace: true,
        state: {
          ...(from.activeTab ? { activeTab: from.activeTab } : {}),
          ...(location.state?.type ? { type: location.state.type } : {}),
        },
      });
      return;
    }
    if (backToTab === "case_history") {
      navigate("/case-management", {
        replace: true,
        state: { activeTab: "case_history" },
      });
      return;
    }

    navigate(-1);
  };
  const getValidationIssueTitle = (type: ValidationIssue["type"]) => {
    switch (type) {
      case "missing":
        return t("Missing_Required_Milestone");
      case "sequence":
        return t("Impossible_Timestamp_Inversion");
      case "amPm":
        return t("Possible_AM_PM_Issue");
      default:
        return t("Validation_MinimumGap_Title");
    }
  };

  const getValidationIssueColors = (isHardBlock?: boolean) => {
    return isHardBlock
      ? {
          bgcolor: "#FEF2F2",
          borderColor: "#FFC9C9",
          textColor: "#E00000",
        }
      : {
          bgcolor: "#FFF7ED",
          borderColor: "#FFD6A8",
          textColor: "#C2410C",
        };
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        flexShrink={0}
        sx={{
          padding: "24px 16px",
          height: "100px",
          boxSizing: "border-box",
          backgroundColor: "custom.background.primary",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        {back && (
          <IconButton
            onClick={handleBackNavigation}
            sx={{
              width: 36,
              height: 36,
              mr: 1.5,
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
            }}
          >
            <ChevronLeft sx={{ width: 20, height: 20, color: "#364153" }} />
          </IconButton>
        )}
        <Box display="flex" flexDirection="column" maxWidth={300} pr={2}>
          <Typography noWrap sx={headerMetaTextSx}>
            {t("MRN_Val", { val: displayMRN })}
          </Typography>
          {caseId && (
            <Typography noWrap sx={{ ...headerMetaTextSx, color: "#000000" }}>
              {t("CaseIdVal", { val: caseId })}
            </Typography>
          )}
        </Box>
        <Divider orientation="vertical" sx={headerDividerSx} />
        <Typography maxWidth={300} noWrap px={2} sx={headerTitleTextSx}>
          {surgeon}
        </Typography>
        <Divider orientation="vertical" sx={headerDividerSx} />
        <Typography
          maxWidth={300}
          noWrap
          px={2}
          sx={{ ...headerTitleTextSx, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {procedureName}
        </Typography>

        <Box flex={1} display="flex" justifyContent="flex-end">
          {showEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              sx={{ ...headerActionButtonSx, px: 2, mr: 2 }}
            >
              {isEditMode ? t("Confirm_Changes") : t("Edit")}
            </Button>
          )}

          {showExport && (
            <Button
              onClick={(event) => handleExport(event, enableDropDownExport)}
              variant="outlined"
              startIcon={!enableDropDownExport && <Icons.Pdf />}
              endIcon={
                enableDropDownExport &&
                (anchorEl ? (
                  <ExpandLessIcon sx={{ width: 26, height: 26 }} />
                ) : (
                  <ExpandMoreIcon sx={{ width: 26, height: 26 }} />
                ))
              }
              sx={{ ...headerActionButtonSx, px: 2 }}
            >
              {t("Export")}
            </Button>
          )}
        </Box>

        <DropDownMenu
          anchorEl={anchorEl}
          onClose={handleClose}
          menuItems={exportMenuItems}
        />
      </Box>
      <CommonModal
        open={isValidationModalOpen}
        onClose={() => setIsValidationModalOpen(false)}
        containerStyle={{ width: 600 }}
        buttonWrapperSx={
          hasHardBlockIssue
            ? { width: "100%", justifyContent: "stretch" }
            : { width: "100%", justifyContent: "space-between" }
        }
        buttonArray={
          hasHardBlockIssue
            ? [
                {
                  text: "Go_Back_And_Correct",
                  onClick: () => setIsValidationModalOpen(false),
                  sx: {
                    width: "100%",
                    height: 48,
                    minWidth: 0,
                    backgroundColor: "#5921F2",
                    color: "#FFFFFF",
                    fontWeight: "700",
                    fontSize: "18px",
                  },
                },
              ]
            : [
                {
                  text: "Go_Back",
                  onClick: () => setIsValidationModalOpen(false),
                  variant: "outlined",
                  sx: {
                    flex: 1,
                    height: 48,
                    minWidth: 0,
                    border: "1px solid #5921F2",
                    color: "#5921F2",
                    backgroundColor: "#FFFFFF",
                    fontWeight: "700",
                    fontSize: "18px",
                  },
                },
                {
                  text: "Confirm_And_Submit",
                  onClick: () => {
                    setIsValidationModalOpen(false);
                    onEditModeChange?.(false);
                  },
                  sx: {
                    flex: 1,
                    height: 48,
                    minWidth: 0,
                    backgroundColor: "#5921F2",
                    color: "#FFFFFF",
                    fontWeight: "700",
                    fontSize: "18px",
                  },
                },
              ]
        }
      >
        <Box mt={1} display="flex" flexDirection="column" gap={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={1}
          >
            {hasHardBlockIssue ? <Icons.CloseIcon /> : <Icons.AlertSubmit />}

            <Typography fontWeight={700} ml={1} fontSize={24}>
              {hasHardBlockIssue
                ? t("Cannot_Submit_Case")
                : t("Pre_Submission_Review")}
            </Typography>
          </Box>
          <Typography textAlign="center" fontSize={16} fontWeight={400} mb={1}>
            {t("Case_Validation_Issues_Intro")}
          </Typography>
        </Box>
        <Box
          mt={2}
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ maxHeight: "55vh", overflowY: "auto", pr: 1 }}
        >
          {showAutoCloseHeadingFromRef && (() => {
            const issueColors = getValidationIssueColors(false);
            return (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: issueColors.borderColor,
                  bgcolor: issueColors.bgcolor,
                  borderRadius: "8px",
                  p: 1.5,
                }}
              >
                <Typography
                  fontWeight={500}
                  fontSize={14}
                  color={issueColors.textColor}
                  mb={0.5}
                >
                  {t("Impossible_Timestamp_Inversion")}
                </Typography>
                <Typography
                  fontSize={14}
                  fontWeight={400}
                  color={issueColors.textColor}
                >
                  {t("auto-close-heading")}
                </Typography>
              </Box>
            );
          })()}
          {validationIssues.map((issue, index) => {
            const issueColors = getValidationIssueColors(issue.isHardBlock);

            return (
              <Box
                key={`${issue.type}-${issue.milestoneName}-${index}`}
                sx={{
                  border: "1px solid",
                  borderColor: issueColors.borderColor,
                  bgcolor: issueColors.bgcolor,
                  borderRadius: "8px",
                  p: 1.5,
                }}
              >
                <Typography
                  fontWeight={500}
                  fontSize={14}
                  color={issueColors.textColor}
                  mb={0.5}
                >
                  {getValidationIssueTitle(issue.type)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: issueColors.textColor,
                    mb: 0.5,
                  }}
                >
                  {issue.type === "missing" &&
                    t("Missing_Required_Milestone_Subheading", {
                      milestone: issue.milestoneName,
                    })}
                  {issue.type === "sequence" &&
                    t("Impossible_Timestamp_Inversion_Before_Subheading", {
                      milestone: issue.milestoneName,
                      time: issue.milestoneTime,
                      prev_milestone: issue.prevMilestoneName,
                      prev_time: issue.prevMilestoneTime,
                    })}
                  {issue.type === "amPm" &&
                    t(
                      "Impossible_Timestamp_Inversion_Not_Sequence_Subheading",
                      {
                        milestone: issue.milestoneName,
                        time: issue.milestoneTime,
                        prev_milestone: issue.prevMilestoneName,
                        prev_time: issue.prevMilestoneTime,
                      }
                    )}
                  {issue.type === "minGap" &&
                    t("Minimum_Gap_Validation_Subheading", {
                      milestone: issue.milestoneName,
                      time: issue.milestoneTime,
                      prev_milestone: issue.prevMilestoneName,
                      prev_time: issue.prevMilestoneTime,
                      minimum_gap: issue.minimumGapMinutes ?? 1,
                    })}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 13,
                    lineHeight: "18px",
                    color: issueColors.textColor,
                  }}
                >
                  {/* keep your existing description logic here */}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CommonModal>
      <CommonModal
        open={isSubmitConfirmModalOpen}
        onClose={() => setIsSubmitConfirmModalOpen(false)}
        containerStyle={{ width: 600, maxWidth: "92vw" }}
        buttonWrapperSx={{
          width: "100%",
          justifyContent: "space-between",
        }}
        buttonArray={[
          {
            text: "Cancel",
            variant: "outlined",
            onClick: () => setIsSubmitConfirmModalOpen(false),
            sx: {
              flex: 1,
              height: 48,
              minWidth: 0,
              borderColor: "#5921F2",
              color: "#5921F2",
              backgroundColor: "#FFFFFF",
            },
          },
          {
            text: "Confirm_And_Submit",
            onClick: () => {
              setIsSubmitConfirmModalOpen(false);
              onEditModeChange?.(false);
            },
            sx: {
              flex: 1,
              height: 48,
              minWidth: 0,
              backgroundColor: "#5921F2",
            },
          },
        ]}
      >
        <Box mt={1} display="flex" flexDirection="column" gap={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={1}
          >
            <CheckCircleOutlineIcon
              sx={{
                width: 24,
                height: 24,
                color: "#2E7D32",
                mr: 1,
              }}
            />
            <Typography fontWeight={700} fontSize={20}>
              {t("Confirm_Case_Submission")}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: "#E8F5E9",
              borderRadius: 2,
              border: "1px solid #A5D6A7",
              p: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <CheckCircleOutlineIcon
              sx={{
                width: 18,
                height: 18,
                color: "#2E7D32",
                mr: 1.5,
              }}
            />
            <Typography fontSize={14} fontWeight={400} color="#1B5E20">
              {t("All_Timestamps_Are_Valid_Ready_To_Submit")}
            </Typography>
          </Box>
        </Box>
      </CommonModal>
    </>
  );
};

export default CaseDetailHeader;
